import type { Writable } from 'node:stream';

/**
 * `--no-color` support.
 *
 * "No color" must strip *color* while preserving non-color display attributes —
 * chiefly `inverse` (reverse video), which the TUI uses to draw the text-input
 * cursor and dropdown selection, plus `bold`/`dim`. chalk and ink only offer an
 * all-or-nothing kill switch (level 0 removes every SGR attribute, cursor
 * included), so instead of disabling them we let them render normally and
 * filter the color escape codes out of the output stream. That removes the
 * 256-color / truecolor sequences that misbehave in some terminals (e.g. GNU
 * screen) while leaving cursor, selection, and layout intact.
 *
 * Precedence (highest first):
 *   1. `--no-color` — explicit opt-out on the command line
 *   2. `NO_COLOR`   — de-facto standard env var (https://no-color.org)
 *   3. `FORCE_COLOR=0` — the supports-color / chalk / ink kill switch
 */

/** Pure predicate, exported for testing. */
export function shouldDisableColor(
  argv: string[] = process.argv,
  env: NodeJS.ProcessEnv = process.env,
): boolean {
  if (argv.includes('--no-color')) return true;
  if (env.NO_COLOR !== undefined && env.NO_COLOR !== '') return true;
  if (env.FORCE_COLOR === '0' || env.FORCE_COLOR === 'false') return true;
  return false;
}

/** True for SGR parameters that set a foreground or background color. */
function isColorParam(code: number): boolean {
  return (
    (code >= 30 && code <= 37) || // standard foreground
    code === 39 || // default foreground
    (code >= 40 && code <= 47) || // standard background
    code === 49 || // default background
    (code >= 90 && code <= 97) || // bright foreground
    (code >= 100 && code <= 107) // bright background
  );
}

/**
 * Remove only the color parameters from a single SGR sequence's parameter
 * string, preserving attributes such as bold (1), dim (2), underline (4) and
 * inverse (7). Returns the tokens to keep (empty ⇒ drop the whole sequence).
 */
function keepNonColorParams(paramStr: string): string[] {
  const tokens = paramStr.split(';');
  const kept: string[] = [];
  let i = 0;
  while (i < tokens.length) {
    const code = Number.parseInt(tokens[i] || '0', 10);
    if (code === 38 || code === 48) {
      // 38/48 introduce extended color: `38;5;n` (256) or `38;2;r;g;b`
      // (truecolor). Drop the introducer and its trailing arguments.
      const mode = Number.parseInt(tokens[i + 1] || '', 10);
      if (mode === 5) i += 3;
      else if (mode === 2) i += 5;
      else i += 2;
    } else if (isColorParam(code)) {
      i += 1;
    } else {
      kept.push(tokens[i] || '0');
      i += 1;
    }
  }
  return kept;
}

/**
 * Strip color SGR (`ESC[…m`) sequences from `input`, leaving every other escape
 * — cursor movement, screen clears, and non-color attributes — untouched.
 */
export function stripColorSgr(input: string): string {
  // eslint-disable-next-line no-control-regex
  return input.replace(/\x1b\[([0-9;]*)m/g, (_match, params: string) => {
    const kept = keepNonColorParams(params);
    return kept.length > 0 ? `\x1b[${kept.join(';')}m` : '';
  });
}

/** Wrap a stream's `write` so string chunks have their color escapes stripped. */
function installColorStripper(stream: Writable): void {
  const original = stream.write.bind(stream) as (...args: unknown[]) => boolean;
  // eslint-disable-next-line no-param-reassign
  stream.write = ((chunk: unknown, ...rest: unknown[]) => {
    const out = typeof chunk === 'string' ? stripColorSgr(chunk) : chunk;
    return original(out, ...rest);
  }) as Writable['write'];
}

/**
 * Decide color enablement and, when disabled, install stdout/stderr filters
 * that drop color escapes. Must be called early (before chalk/ink load) so the
 * filter is in place before the TUI renders its first frame.
 */
export function initColor(): void {
  if (!shouldDisableColor()) return;

  // chalk's supports-color sniffs the `--no-color` argv flag and forces level 0,
  // which strips *all* styling — including the `inverse` attribute the TUI uses
  // to draw the cursor and selection. On a TTY, override that with FORCE_COLOR
  // so ink/chalk keep emitting attributes (and colors); the stripper below then
  // removes only the colors. This must run before chalk loads — see the
  // `init_color_early` bootstrap module imported at the top of index.tsx.
  // Skipped for non-TTY output so piped/redirected streams stay fully plain.
  if (process.stdout.isTTY) {
    // Drop NO_COLOR first: Node warns ("NO_COLOR is ignored due to FORCE_COLOR")
    // when both are set. We've already read it into our decision above, so
    // removing it is safe and makes the NO_COLOR path match the --no-color path.
    delete process.env.NO_COLOR;
    process.env.FORCE_COLOR = '3';
  }

  installColorStripper(process.stdout);
  installColorStripper(process.stderr);
}
